/**
 * AdminPremiumCRM - Panel de gestión de clientes premium
 * Sistema CRM con planes, renovaciones, estados y alertas
 * Mobile-first, diseño GOATGYM
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { 
  Crown,
  AlertTriangle,
  Clock,
  Calendar,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Search,
  User,
  DollarSign,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  Phone,
  RotateCcw,
  Eye,
  Filter
} from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Plan types
const PLAN_TYPES = {
  trimestral: { label: "Trimestral", months: 3, price: 410 },
  anual: { label: "Anual", months: 12, price: 975 }
};

// CRM Status definitions
const CRM_STATUSES = {
  lead: { label: "Lead", color: "bg-blue-500/20 text-blue-400", icon: User },
  llamada_agendada: { label: "Llamada agendada", color: "bg-purple-500/20 text-purple-400", icon: Phone },
  pendiente_pago: { label: "Pendiente pago", color: "bg-yellow-500/20 text-yellow-400", icon: DollarSign },
  activo: { label: "Activo", color: "bg-green-500/20 text-green-400", icon: CheckCircle },
  riesgo_abandono: { label: "Riesgo abandono", color: "bg-orange-500/20 text-orange-400", icon: AlertTriangle },
  renovacion_pendiente: { label: "Renovación pendiente", color: "bg-[#D4AF37]/20 text-[#D4AF37]", icon: RotateCcw },
  renovado: { label: "Renovado", color: "bg-green-500/20 text-green-400", icon: TrendingUp },
  caducado: { label: "Caducado", color: "bg-red-500/20 text-red-400", icon: XCircle },
  cancelado: { label: "Cancelado", color: "bg-gray-500/20 text-gray-400", icon: XCircle }
};

// Plan status colors
const PLAN_STATUS_COLORS = {
  activo: "text-green-400",
  caduca_pronto_15: "text-yellow-400",
  caduca_pronto_7: "text-orange-400",
  urgente: "text-red-400",
  caducado: "text-red-500",
  sin_plan: "text-gray-500"
};

// Filters
const FILTERS = [
  { id: "all", label: "Todos" },
  { id: "activo", label: "Activos" },
  { id: "caduca_7", label: "Caduca 7 días" },
  { id: "caducado", label: "Caducados" },
  { id: "lead", label: "Leads" },
  { id: "renovacion_pendiente", label: "Renovación" }
];

// ============================================
// CLIENT CARD COMPONENT
// ============================================
const PremiumClientCard = ({ client, onClick }) => {
  const plan = client.plan || {};
  const statusConfig = CRM_STATUSES[client.crm_status] || CRM_STATUSES.lead;
  const StatusIcon = statusConfig.icon;
  
  const formatDaysLeft = (days) => {
    if (days === null || days === undefined) return "Sin plan";
    if (days < 0) return `Caducado hace ${Math.abs(days)}d`;
    if (days === 0) return "Caduca hoy";
    if (days === 1) return "Caduca mañana";
    return `${days} días`;
  };

  return (
    <div
      onClick={() => onClick(client)}
      className={cn(
        "p-4 rounded-xl bg-[#121212] border transition-all cursor-pointer",
        "hover:bg-[#1A1A1A] active:scale-[0.99]",
        plan.status === "urgente" && "border-red-500/50",
        plan.status === "caduca_pronto_7" && "border-orange-500/50",
        plan.status === "caducado" && "border-red-500/30",
        !["urgente", "caduca_pronto_7", "caducado"].includes(plan.status) && "border-[#2A2A2A]"
      )}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
          <Crown className="w-5 h-5 text-[#D4AF37]" />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="font-semibold text-white truncate">{client.name}</p>
            {plan.status === "urgente" && (
              <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 animate-pulse" />
            )}
          </div>
          
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Badge className={cn("text-[10px] border-0", statusConfig.color)}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusConfig.label}
            </Badge>
            {plan.type && (
              <Badge variant="outline" className="text-[10px] border-[#2A2A2A] text-[#888]">
                {PLAN_TYPES[plan.type]?.label || plan.type}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-4 text-xs text-[#666]">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              <span className={PLAN_STATUS_COLORS[plan.status]}>
                {formatDaysLeft(plan.days_left)}
              </span>
            </span>
            {plan.price && (
              <span className="flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                {plan.price}€
              </span>
            )}
          </div>
        </div>

        <ChevronRight className="w-5 h-5 text-[#444] flex-shrink-0" />
      </div>
    </div>
  );
};

// ============================================
// PLAN EDITOR MODAL
// ============================================
const PlanEditorModal = ({ client, isOpen, onClose, onSave, isRenewal = false }) => {
  const [planType, setPlanType] = useState("trimestral");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(planType, notes);
      onClose();
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const selectedPlan = PLAN_TYPES[planType];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-[#D4AF37]">
            {isRenewal ? "Renovar Plan" : "Asignar Plan"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Client info */}
          <div className="p-3 rounded-lg bg-[#1A1A1A] border border-[#2A2A2A]">
            <p className="text-white font-medium">{client?.name}</p>
            <p className="text-[#666] text-xs">{client?.email}</p>
          </div>

          {/* Plan type selector */}
          <div>
            <label className="text-[#888] text-xs block mb-2">Tipo de plan</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(PLAN_TYPES).map(([key, plan]) => (
                <button
                  key={key}
                  onClick={() => setPlanType(key)}
                  className={cn(
                    "p-4 rounded-xl border text-center transition-all",
                    planType === key
                      ? "bg-[#D4AF37]/10 border-[#D4AF37] text-[#D4AF37]"
                      : "bg-[#1A1A1A] border-[#2A2A2A] text-[#888] hover:border-[#3A3A3A]"
                  )}
                >
                  <p className="font-bold text-lg">{plan.price}€</p>
                  <p className="text-xs opacity-80">{plan.label}</p>
                  <p className="text-[10px] opacity-60">{plan.months} meses</p>
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-[#888] text-xs block mb-1">Notas (opcional)</label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notas internas..."
              className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
            />
          </div>

          {/* Summary */}
          <div className="p-3 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30">
            <p className="text-[#D4AF37] text-sm font-medium">
              {isRenewal ? "Renovación" : "Nuevo plan"}: {selectedPlan.label}
            </p>
            <p className="text-[#D4AF37]/80 text-xs">
              Duración: {selectedPlan.months} meses • Precio: {selectedPlan.price}€
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-[#2A2A2A] text-white"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-[#D4AF37] hover:bg-[#B8962E] text-[#0B0B0B]"
            >
              {saving ? "Guardando..." : (isRenewal ? "Renovar" : "Asignar")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// STATUS EDITOR MODAL
// ============================================
const StatusEditorModal = ({ client, isOpen, onClose, onSave }) => {
  const [status, setStatus] = useState(client?.crm_status || "activo");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(status, notes);
      onClose();
    } catch (error) {
      toast.error("Error al actualizar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-white">Cambiar Estado CRM</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {Object.entries(CRM_STATUSES).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <button
                  key={key}
                  onClick={() => setStatus(key)}
                  className={cn(
                    "w-full p-3 rounded-lg border flex items-center gap-3 transition-all text-left",
                    status === key
                      ? "bg-[#D4AF37]/10 border-[#D4AF37]"
                      : "bg-[#1A1A1A] border-[#2A2A2A] hover:border-[#3A3A3A]"
                  )}
                >
                  <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", config.color.split(" ")[0])}>
                    <Icon className={cn("w-4 h-4", config.color.split(" ")[1])} />
                  </div>
                  <span className={cn("font-medium", status === key ? "text-[#D4AF37]" : "text-white")}>
                    {config.label}
                  </span>
                </button>
              );
            })}
          </div>

          <div>
            <label className="text-[#888] text-xs block mb-1">Notas (opcional)</label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Motivo del cambio..."
              className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-[#2A2A2A] text-white"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-[#D4AF37] hover:bg-[#B8962E] text-[#0B0B0B]"
            >
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const AdminPremiumCRM = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [dashboard, setDashboard] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Modals
  const [selectedClient, setSelectedClient] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [isRenewal, setIsRenewal] = useState(false);

  const fetchData = useCallback(async (showToast = false) => {
    try {
      if (showToast) setRefreshing(true);
      
      const [dashRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/premium/dashboard`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/premium/clients`, { headers: getAuthHeaders() })
      ]);
      
      setDashboard(dashRes.data);
      setClients(clientsRes.data.clients || []);
      
      if (showToast) toast.success("Datos actualizados");
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error cargando datos");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter clients
  const filteredClients = useMemo(() => {
    let result = [...clients];
    
    // Apply filter
    switch (activeFilter) {
      case "activo":
        result = result.filter(c => c.crm_status === "activo" && c.plan?.status === "activo");
        break;
      case "caduca_7":
        result = result.filter(c => c.plan?.days_left !== null && c.plan?.days_left <= 7 && c.plan?.days_left >= 0);
        break;
      case "caducado":
        result = result.filter(c => c.plan?.status === "caducado" || c.plan?.days_left < 0);
        break;
      case "lead":
        result = result.filter(c => c.crm_status === "lead" || !c.plan?.type);
        break;
      case "renovacion_pendiente":
        result = result.filter(c => c.crm_status === "renovacion_pendiente");
        break;
      default:
        break;
    }
    
    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(c => 
        c.name?.toLowerCase().includes(term) ||
        c.email?.toLowerCase().includes(term)
      );
    }
    
    return result;
  }, [clients, activeFilter, searchTerm]);

  // Handlers
  const handleClientClick = (client) => {
    setSelectedClient(client);
    // Could navigate to detail page or open modal
  };

  const handleAssignPlan = (client) => {
    setSelectedClient(client);
    setIsRenewal(false);
    setShowPlanModal(true);
  };

  const handleRenewPlan = (client) => {
    setSelectedClient(client);
    setIsRenewal(true);
    setShowPlanModal(true);
  };

  const handleChangeStatus = (client) => {
    setSelectedClient(client);
    setShowStatusModal(true);
  };

  const savePlan = async (planType, notes) => {
    const endpoint = isRenewal
      ? `${API_URL}/api/admin/premium/clients/${selectedClient.id}/renew`
      : `${API_URL}/api/admin/premium/clients/${selectedClient.id}/plan`;
    
    const method = isRenewal ? "post" : "put";
    
    await axios[method](endpoint, { plan_type: planType, notes }, { headers: getAuthHeaders() });
    toast.success(isRenewal ? "Plan renovado" : "Plan asignado");
    fetchData();
  };

  const saveStatus = async (status, notes) => {
    await axios.put(
      `${API_URL}/api/admin/premium/clients/${selectedClient.id}/status`,
      { status, notes },
      { headers: getAuthHeaders() }
    );
    toast.success("Estado actualizado");
    fetchData();
  };

  const stats = dashboard?.stats || {};

  return (
    <div className="min-h-screen bg-[#0B0B0B] pb-24" data-testid="admin-premium-crm">
      {/* Header */}
      <div className="sticky top-0 bg-[#0B0B0B]/95 backdrop-blur-sm border-b border-[#2A2A2A] p-4 z-10">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate("/admin")} className="p-2 -ml-2 text-white hover:bg-white/10 rounded-lg">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-white font-bold text-lg flex items-center gap-2">
              <Crown className="w-5 h-5 text-[#D4AF37]" />
              Premium CRM
            </h1>
            <p className="text-[#666] text-xs">
              {clients.length} clientes premium
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="text-[#666] hover:text-[#D4AF37]"
          >
            <RefreshCw className={cn("w-5 h-5", refreshing && "animate-spin")} />
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="p-2 rounded-lg bg-[#1A1A1A] border border-[#2A2A2A] text-center">
            <p className="text-lg font-bold text-[#D4AF37]">{stats.total_premium || 0}</p>
            <p className="text-[10px] text-[#666]">Total</p>
          </div>
          <div className="p-2 rounded-lg bg-green-500/10 border border-green-500/30 text-center">
            <p className="text-lg font-bold text-green-400">{stats.activos || 0}</p>
            <p className="text-[10px] text-[#666]">Activos</p>
          </div>
          <div 
            onClick={() => setActiveFilter("caduca_7")}
            className="p-2 rounded-lg bg-orange-500/10 border border-orange-500/30 text-center cursor-pointer hover:bg-orange-500/20"
          >
            <p className="text-lg font-bold text-orange-400">{stats.caduca_7_dias || 0}</p>
            <p className="text-[10px] text-[#666]">Caduca 7d</p>
          </div>
          <div 
            onClick={() => setActiveFilter("caducado")}
            className="p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-center cursor-pointer hover:bg-red-500/20"
          >
            <p className="text-lg font-bold text-red-400">{stats.caducados || 0}</p>
            <p className="text-[10px] text-[#666]">Caducados</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar cliente..."
            className="pl-10 bg-[#1A1A1A] border-[#2A2A2A] text-white"
          />
        </div>

        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {FILTERS.map(filter => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={cn(
                "px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all",
                activeFilter === filter.id
                  ? "bg-[#D4AF37] text-[#0B0B0B]"
                  : "bg-[#1A1A1A] text-[#888] hover:bg-[#2A2A2A]"
              )}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Alerts */}
      {dashboard?.alerts?.length > 0 && (
        <div className="px-4 pt-4">
          <Card className="bg-red-500/10 border-red-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-red-400 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Alertas de renovación
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {dashboard.alerts.slice(0, 3).map((alert, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-red-500/20 last:border-0">
                  <div>
                    <p className="text-white text-sm">{alert.email?.split("@")[0]}</p>
                    <p className="text-red-400 text-xs">{alert.message}</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      const client = clients.find(c => c.id === alert.user_id);
                      if (client) handleRenewPlan(client);
                    }}
                    className="bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs h-7"
                  >
                    Renovar
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Clients List */}
      <div className="p-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-20">
            <Crown className="w-12 h-12 text-[#333] mx-auto mb-4" />
            <p className="text-[#666]">No hay clientes en este filtro</p>
          </div>
        ) : (
          filteredClients.map(client => (
            <div key={client.id}>
              <PremiumClientCard
                client={client}
                onClick={handleClientClick}
              />
              {/* Quick Actions */}
              <div className="flex gap-2 mt-2 ml-12">
                {!client.plan?.type ? (
                  <Button
                    size="sm"
                    onClick={() => handleAssignPlan(client)}
                    className="bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 text-[#D4AF37] text-xs h-7"
                  >
                    Asignar plan
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => handleRenewPlan(client)}
                    className="bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 text-[#D4AF37] text-xs h-7"
                  >
                    Renovar
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleChangeStatus(client)}
                  className="text-[#888] hover:text-white text-xs h-7"
                >
                  Estado
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate(`/admin/premium-clients?client=${client.id}`)}
                  className="text-[#888] hover:text-white text-xs h-7"
                >
                  <Eye className="w-3 h-3 mr-1" /> Ver
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Plan Modal */}
      <PlanEditorModal
        client={selectedClient}
        isOpen={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        onSave={savePlan}
        isRenewal={isRenewal}
      />

      {/* Status Modal */}
      <StatusEditorModal
        client={selectedClient}
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        onSave={saveStatus}
      />
    </div>
  );
};

export default AdminPremiumCRM;
